import { type Employee, projectTeamResponseSchema } from '@sivan/shared';
import { useId, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../api/client';
import { useLoad, useWorkspace } from '../master-data/use-master-data';

/**
 * Step 2 of adding an employee ("גישה"): a project, a responsibility shown in the team, and the
 * project role templates (what the employee may do there). The same server calls and checks as
 * the project team screen: only an access manager may do this, and the server decides.
 */
export function AccessStep({
  employee,
  explain,
  onDone,
  onSkip,
}: {
  readonly employee: Employee;
  readonly explain: (error: unknown) => string;
  readonly onDone: (summary: string) => void;
  readonly onSkip: () => void;
}) {
  const id = useId();
  const [workspace] = useWorkspace();
  const projects = workspace.state === 'ready' ? workspace.data.projects : [];
  const [projectId, setProjectId] = useState('');
  const [responsibility, setResponsibility] = useState('');
  const [chosen, setChosen] = useState<readonly string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [team] = useLoad(`access-step:${projectId}`, () =>
    projectId === ''
      ? Promise.resolve(null)
      : apiGet(`/projects/${projectId}/team`, projectTeamResponseSchema),
  );
  // Project templates only: organization-wide roles are set in "תפקיד ארגוני".
  const templates =
    team.state === 'ready' && team.data !== null
      ? team.data.templates.filter((t) =>
          t.permissions.every((p) => !p.startsWith('organization.')),
        )
      : [];
  const project = projects.find((p) => p.id === projectId);
  const ready = projectId !== '' && responsibility.trim().length >= 2 && chosen.length > 0;
  const save = () => {
    setBusy(true);
    setError(null);
    (async () => {
      const already =
        team.state === 'ready' && team.data?.members.some((m) => m.employee.id === employee.id);
      if (already !== true)
        await apiPost(
          `/projects/${projectId}/team`,
          { employeeId: employee.id, responsibility: responsibility.trim() },
          projectTeamResponseSchema,
        );
      await apiPut(
        `/projects/${projectId}/team/${employee.id}/roles`,
        { templateIds: chosen },
        projectTeamResponseSchema,
      );
    })()
      .then(() => {
        const names = templates.filter((t) => chosen.includes(t.id)).map((t) => t.name);
        onDone(`${employee.fullName} צורף/ה לפרויקט ${project?.name ?? ''}: ${names.join(', ')}.`);
      })
      .catch((e: unknown) => {
        setError(explain(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  return (
    <div className="wizard-step">
      <p className="hint">
        באיזה פרויקט {employee.fullName} יעבוד/תעבוד, ומה יוכל/תוכל לעשות בו. אפשר להוסיף פרויקטים
        ותפקידים נוספים אחר כך במסך צוות הפרויקט.
      </p>
      <label className="field" htmlFor={`${id}-project`}>
        <span>פרויקט</span>
        <select
          id={`${id}-project`}
          value={projectId}
          onChange={(e) => {
            setProjectId(e.target.value);
            setChosen([]);
          }}
        >
          <option value="">בחירת פרויקט</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>
      <label className="field" htmlFor={`${id}-responsibility`}>
        <span>תחום אחריות (לתצוגה בלבד)</span>
        <input
          id={`${id}-responsibility`}
          value={responsibility}
          maxLength={80}
          placeholder="למשל: מפקח בניין A"
          onChange={(e) => {
            setResponsibility(e.target.value);
          }}
        />
      </label>
      {projectId !== '' && (
        <fieldset className="wizard-step__roles">
          <legend>מה יוכל/תוכל לעשות בפרויקט</legend>
          {team.state === 'loading' && <p className="hint">טוען…</p>}
          {templates.map((t) => (
            <label key={t.id} className="choice">
              <input
                type="checkbox"
                checked={chosen.includes(t.id)}
                onChange={(e) => {
                  const on = e.target.checked;
                  setChosen((list) => (on ? [...list, t.id] : list.filter((x) => x !== t.id)));
                }}
              />
              <span>{t.name}</span>
            </label>
          ))}
        </fieldset>
      )}
      {error !== null && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}
      <div className="form-actions">
        <button
          type="button"
          className="button button--primary"
          disabled={!ready || busy}
          onClick={save}
        >
          {busy ? 'שומר…' : 'שמירה והמשך'}
        </button>
        <button type="button" className="button" onClick={onSkip}>
          דילוג (גישה תינתן אחר כך)
        </button>
      </div>
    </div>
  );
}
